import React, {FC, useState} from "react"

import {H1, H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {LangLoader, InjectIntl} from "@irati/utils/lang"
import {ONBOARDING} from "@irati-web/app/src/routeConfig"
import Button from "@irati/ui/src/Button"
import {useTheme} from "@irati/ui/src/global/useTheme"

import welcomeImg from "../../images/welcome.png"
import benefitsDiscountImg from "../../images/benefitsDiscounts.png"
import rewardRecognitionImg from "../../images/rewardRecognition.png"
import cultureCommunicationImg from "../../images/cultureCommunication.png"
import wellbeingSupportImg from "../../images/wellbeingSupport.png"
import automateProcessesImg from "../../images/automateProcesses.png"
import globeImg from "../../images/globe.png"
import MSTeamsImg from "../../images/MSTeams.png"
import insightsImg from "../../images/insights.png"
import setupWidgetImg from "../../images/setupWidget.png"
import perkboxWhiteImg from "../../images/perkboxWhite.png"

import ProgressBar from "../LandingStepper"
import {Block, Background, StyledCard, Gradient, Img, ResponsiveWrapper} from "./styled"
import Content from "./Content"

interface Props extends InjectIntl {
  onConfirm: () => void
}

const AdminOnboarding: FC<Props> = ({onConfirm}) => {
  const handleNext = () => {
    if (step === 4) {
      onConfirm()
    } else {
      setStep(step + 1)
    }
  }

  const [step, setStep] = useState(1)
  const theme = useTheme()

  return (
    <LangLoader buckets={[ONBOARDING]}>
      <Background step={step}>
        <img src={perkboxWhiteImg} alt="perkboxWhiteImg" height={37} />
        <Block>
          {step === 4 ? (
            <H1 marginTop={2} marginBottom={9} textAlign="center">
              <FormattedMessage id="onboarding.adminWelcome.title.getStarted" />
            </H1>
          ) : (
            <H1 marginTop={2} marginBottom={4} textAlign="center">
              <FormattedMessage id="onboarding.adminWelcome.title.welcome" />
            </H1>
          )}

          {step !== 4 && <ProgressBar selected={step} />}
          <Gradient step={step} />
          <StyledCard marginBottom={4}>
            {step === 1 && (
              <Flex alignItems="center" gap={theme.spacing.eight} alignSelf="center" wrap="wrap">
                <Img src={welcomeImg} height={237} />
                <ResponsiveWrapper>
                  <H2 marginBottom={4}>
                    <FormattedMessage id="onboarding.adminWelcome.step1.title" />
                  </H2>
                  <Body marginBottom={2}>
                    <FormattedMessage id="onboarding.adminWelcome.step1.body" />
                  </Body>
                </ResponsiveWrapper>
              </Flex>
            )}
            {step === 2 && (
              <div>
                <H2 marginBottom={4}>
                  <FormattedMessage id="onboarding.adminWelcome.step2.title" />
                </H2>
                <Flex marginBottom={3} gap={theme.spacing.five} wrap="wrap">
                  <Content
                    title="onboarding.adminWelcome.step2.content1.title"
                    text="onboarding.adminWelcome.step2.content1.text"
                    img={rewardRecognitionImg}
                    alt="rewardRecognitionImg"
                  />
                  <Content
                    title="onboarding.adminWelcome.step2.content2.title"
                    text="onboarding.adminWelcome.step2.content2.text"
                    img={benefitsDiscountImg}
                    alt="benefitsDiscountImg"
                  />
                </Flex>
                <Flex gap={theme.spacing.five} wrap="wrap">
                  <Content
                    title="onboarding.adminWelcome.step2.content3.title"
                    text="onboarding.adminWelcome.step2.content3.text"
                    img={cultureCommunicationImg}
                    alt="cultureCommunication"
                  />
                  <Content
                    title="onboarding.adminWelcome.step2.content4.title"
                    text="onboarding.adminWelcome.step2.content4.text"
                    img={wellbeingSupportImg}
                    alt="ellbeingSupportImg"
                  />
                </Flex>
              </div>
            )}
            {step === 3 && (
              <div>
                <H2 marginBottom={6}>
                  <FormattedMessage id="onboarding.adminWelcome.step3.title" />
                </H2>
                <Flex marginBottom={8} gap={theme.spacing.four} wrap="wrap">
                  <Content
                    text="onboarding.adminWelcome.step3.content1.text"
                    img={automateProcessesImg}
                    alt="automateProcessesImg"
                    alignItems="flex-start"
                    height={60}
                  />
                  <Content
                    text="onboarding.adminWelcome.step3.content2.text"
                    img={MSTeamsImg}
                    alt="MSTeamsImg"
                    alignItems="flex-start"
                    height={60}
                  />
                </Flex>
                <Flex gap={theme.spacing.four} wrap="wrap">
                  <Content height={60} text="onboarding.adminWelcome.step3.content3.text" img={globeImg} alt="globeImg" />
                  <Content height={60} text="onboarding.adminWelcome.step3.content4.text" img={insightsImg} alt="insightsImg" />
                </Flex>
              </div>
            )}
            {step === 4 && (
              <Flex direction="column" alignItems="center" margin="0 auto">
                <Img src={setupWidgetImg} alt="setupWidgetImg" height={163} />
                <H2 marginTop={4} marginBottom={2}>
                  <FormattedMessage id="onboarding.adminWelcome.step4.title" />
                </H2>
                <Body small maxWidth="350px" textAlign="center">
                  <FormattedMessage id="onboarding.adminWelcome.step4.text" />
                </Body>
              </Flex>
            )}
          </StyledCard>
          <Flex maxWidth="500px" margin="0 auto">
            <Button full primary onClick={handleNext}>
              {step === 4 ? (
                <FormattedMessage id="onboarding.adminWelcome.button.getStarted" />
              ) : (
                <FormattedMessage id="onboarding.adminWelcome.button.next" />
              )}
            </Button>
          </Flex>
        </Block>
      </Background>
    </LangLoader>
  )
}

export default AdminOnboarding
